import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { FiMapPin } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const Payment = () => {
  const { cartItems, clearCart } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] = useState("cod"); // default COD
  const [form, setForm] = useState({
    name: "",
    cardNumber: "",
    expiry: "",
    cvv: "",
  });
  const [showEsewaQR, setShowEsewaQR] = useState(false);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);

  const totalPrice = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const getLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await res.json();
            const address =
              data.display_name || `Lat: ${latitude}, Lng: ${longitude}`;
            setLocation({ latitude, longitude, address });
          } catch (err) {
            console.error("Error fetching address:", err);
            setLocation({
              latitude,
              longitude,
              address: `Lat: ${latitude}, Lng: ${longitude}`,
            });
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          setLoading(false);
          alert("Unable to get your location. Please enable location access.");
          console.error(error);
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      alert("You must be logged in to place an order.");
      return;
    }

    if (!location) {
      alert("Please get your location before placing the order.");
      return;
    }

    setLoading(true);

    try {
      const docRef = await addDoc(collection(db, "orders"), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        paymentMethod,
        cartItems,
        totalPrice,
        createdAt: serverTimestamp(),
        cardDetails: paymentMethod === "card" ? form : null,
        status: "Pending",
        location,
      });

      clearCart();
      alert("Order placed successfully!");
      navigate(`/order-tracking/${docRef.id}`);
    } catch (error) {
      console.error("Error placing order:", error);
      alert("Failed to place order. Please try again.");
    }

    setLoading(false);
  };

  const getButtonText = () => {
    if (loading) return "Processing...";
    return paymentMethod === "cod" ? "Place Order" : "Pay Now";
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white shadow-xl rounded-2xl max-w-4xl w-full flex flex-col md:flex-row overflow-hidden">
        {/* Form Section */}
        <form onSubmit={handleSubmit} className="w-full md:w-2/3 p-8 space-y-6">
          <h2 className="text-3xl font-bold text-gray-800">Secure Payment</h2>

          {/* Payment Method */}
          <div className="space-y-2">
            <label className="block text-gray-700 font-semibold">Select Payment Method</label>
            <div className="flex flex-col space-y-2">
              {["card", "esewa", "khalti", "cod"].map((method) => (
                <label key={method} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={method}
                    checked={paymentMethod === method}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <span className="capitalize">{method === "cod" ? "Cash on Delivery" : method}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <button
              type="button"
              onClick={getLocation}
              disabled={loading}
              className={`flex items-center justify-center gap-2 w-full md:w-auto px-5 py-3 rounded-lg font-medium transition-all duration-300
                ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
            >
              <FiMapPin className="w-5 h-5" />
              {loading ? "Getting location..." : "Use Current Location"}
            </button>

            {location && (
              <p className="text-sm text-gray-700 mt-2">📍 {location.address}</p>
            )}
          </div>

          {/* Card Form */}
          <div className="hidden">
            <div>
              <label className="block text-gray-600 mb-1">Cardholder Name</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                type="text"
                placeholder="John Doe"
                required
                disabled={paymentMethod !== "card"}
                className={`w-full px-4 py-2 border rounded-lg ${paymentMethod !== "card" ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Card Number</label>
              <input
                name="cardNumber"
                value={form.cardNumber}
                onChange={handleChange}
                type="text"
                placeholder="1234 5678 9012 3456"
                required
                disabled={paymentMethod !== "card"}
                className={`w-full px-4 py-2 border rounded-lg ${paymentMethod !== "card" ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
            </div>

            <div className="flex gap-4">
              <input
                name="expiry"
                value={form.expiry}
                onChange={handleChange}
                type="text"
                placeholder="MM/YY"
                required
                disabled={paymentMethod !== "card"}
                className={`w-1/2 px-4 py-2 border rounded-lg ${paymentMethod !== "card" ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
              <input
                name="cvv"
                value={form.cvv}
                onChange={handleChange}
                type="password"
                placeholder="CVV"
                required
                disabled={paymentMethod !== "card"}
                className={`w-1/2 px-4 py-2 border rounded-lg ${paymentMethod !== "card" ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
            </div>
          </div>

          {/* Other Payment Messages */}
          <p className={`mt-2 ${paymentMethod !== "cod" ? "text-gray-400" : "text-gray-700"}`}>
            {paymentMethod === "esewa" && "Click Pay Now to view eSewa QR for payment."}
            {paymentMethod === "khalti" && "Khalti integration coming soon…"}
            {paymentMethod === "cod" && "You will pay in cash when the item is delivered."}
          </p>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || paymentMethod !== "cod"}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-lg transition disabled:opacity-50"
          >
            {getButtonText()}
          </button>
        </form>

        {/* Order Summary */}
        <div className="bg-indigo-50 w-full md:w-1/3 p-6 md:p-8 flex flex-col justify-between">
          <div>
            <h3 className="text-xl font-semibold mb-4">Order Summary</h3>
            <ul className="text-gray-700 space-y-2 max-h-80 overflow-y-auto pr-2">
              {cartItems.map((item) => (
                <li key={item.id} className="flex justify-between">
                  <span>{item.name} × {item.quantity}</span>
                  <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                </li>
              ))}
              <li className="flex justify-between font-semibold border-t pt-2 mt-2">
                <span>Total</span>
                <span>₹{totalPrice.toFixed(2)}</span>
              </li>
            </ul>
          </div>

          <div className="mt-8 text-sm text-gray-500">
            <p>Your payment is encrypted and secure.</p>
            <p className="mt-1">Powered by SecurePay™</p>
          </div>
        </div>
      </div>

      {/* eSewa QR Modal */}
      {showEsewaQR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-xl text-center">
            <h2 className="text-2xl font-bold mb-4 text-green-700">Scan with eSewa</h2>
            <img
              src="https://pbs.twimg.com/media/Eo0vEA8VgAAdU0n.png"
              alt="eSewa QR"
              className="w-60 h-80 mx-auto mb-4"
            />
            <button
              onClick={() => setShowEsewaQR(false)}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payment;
